import { debounce } from 'lodash';
import { useRef, useMemo, useState, useEffect } from 'react';

import { checkLoanBadDebt } from 'src/redux/slices/loan-agreement';

import {
  NIC_PATTERN,
  PHONE_PATTERN,
  IBadDebtMatch,
  LoanBorrowerType,
} from 'src/types/loan-agreement';

type BorrowerValues = {
  borrowerType: LoanBorrowerType;
  employee?: string | null;
  borrowerName?: string;
  nicNumber?: string;
  phoneNumber?: string;
};

const compact = (value?: string) => (value ?? '').replace(/[\s-]/g, '');

/**
 * Runs the server-side bad-debt check (debounced) whenever the borrower's
 * identity changes. Only valid NIC/phone values are sent, so a half-typed
 * NIC doesn't count as "entered" and lift a name-only match early.
 */
export function useBadDebtCheck(
  values: BorrowerValues,
  options: { excludeAgreement?: string; enabled?: boolean } = {}
) {
  const { excludeAgreement, enabled = true } = options;
  const [matches, setMatches] = useState<IBadDebtMatch[]>([]);
  const [checking, setChecking] = useState(false);
  const requestId = useRef(0);

  const nic = values.nicNumber?.trim() ?? '';
  const phone = compact(values.phoneNumber);

  const payload = useMemo(() => {
    if (!enabled) {
      return null;
    }
    if (values.borrowerType === LoanBorrowerType.EMPLOYEE) {
      return values.employee
        ? { borrowerType: LoanBorrowerType.EMPLOYEE, employee: values.employee }
        : null;
    }
    const validNic = NIC_PATTERN.test(nic) ? nic : undefined;
    const validPhone = PHONE_PATTERN.test(phone) ? phone : undefined;
    const name = values.borrowerName?.trim();
    if (!validNic && !validPhone && !name) {
      return null;
    }
    return {
      borrowerType: LoanBorrowerType.OTHER,
      borrowerName: name || undefined,
      nicNumber: validNic,
      phoneNumber: validPhone,
    };
  }, [enabled, values.borrowerType, values.employee, values.borrowerName, nic, phone]);

  const runCheck = useMemo(
    () =>
      debounce(async (body: NonNullable<typeof payload>, id: number) => {
        try {
          const result = await checkLoanBadDebt({ ...body, excludeAgreement });
          if (id === requestId.current) setMatches(result);
        } catch {
          if (id === requestId.current) setMatches([]);
        } finally {
          if (id === requestId.current) setChecking(false);
        }
      }, 400),
    [excludeAgreement]
  );

  useEffect(() => {
    requestId.current += 1;
    if (!payload) {
      runCheck.cancel();
      setMatches([]);
      setChecking(false);
      return;
    }
    setChecking(true);
    runCheck(payload, requestId.current);
  }, [payload, runCheck]);

  useEffect(() => () => runCheck.cancel(), [runCheck]);

  return { matches, checking };
}
